import { SelectOption } from '../../types';
import CurrencyInput from './CurrencyInput';
import { FeeType, Form } from '../types';
import ControlledSelect from './ControlledSelect';
import PercentInput from './PercentInput';
import inputFieldStyles from '../sharedStyles/InputField.module.css';
import InsuranceProviderSegmentedControl from './InsuranceProviderSegmentedControl';
import InsuranceCasesSection from './InsuranceCasesSection/InsuranceCasesSection';
import { colorsForForm } from '../colorsForForm';

const FeeTypeFields = ({
  className,
  feeType,
  attorneys,
  form,
}: {
  className?: string;
  feeType: FeeType | undefined;
  attorneys: SelectOption[];
  form: Form;
}) => {
  if (!feeType) {
    return null;
  }

  const activeColors = colorsForForm(form);

  return (
    <div
      className={className}
      style={
        {
          '--active-background-color': activeColors.background,
        } as React.CSSProperties
      }
    >
      {feeType === 'Insurance' && <InsuranceFeeTypeFields form={form} />}

      {feeType === 'Hourly' && (
        <>
          <div className={inputFieldStyles.inputRow}>
            <CurrencyInput form={form} name='retainer' label='Retainer' />
            <CurrencyInput
              // `form.key(path)` forces the underlying uncontrolled input to
              // remount when `setFieldValue` is called programmatically (e.g.
              // by the new-matter auto-populate in `AddEditMatterForm.tsx`
              // when the responsible attorney is swapped). Without this, the
              // input reads `defaultValue` only on mount and keeps showing
              // the stale number even though the form's internal ref is
              // correct. Matches the pattern already used for `matterName`,
              // `description`, and `scope` in MatterDetailsSection.
              key={form.key('responsibleAttyHourlyRate')}
              form={form}
              name='responsibleAttyHourlyRate'
              label='Responsible Atty Hourly Rate'
            />
          </div>
          <div className={inputFieldStyles.inputRow}>
            <ControlledSelect
              fieldName='referralOriginatorId'
              label='Referral Originator'
              options={attorneys}
              form={form}
              clearable={false}
            />
            <ControlledSelect
              fieldName='engagementOriginatorId'
              label='Engagement Originator'
              options={attorneys}
              form={form}
              clearable={false}
            />
          </div>
        </>
      )}
      {feeType === 'FlatFee' && (
        <>
          <div className={inputFieldStyles.inputRow}>
            <CurrencyInput
              form={form}
              name='flatFeeAmt'
              label='Flat Fee Amount'
            />
          </div>
          <div className={inputFieldStyles.inputRow}>
            <ControlledSelect
              fieldName='referralOriginatorId'
              label='Referral Originator'
              options={attorneys}
              form={form}
              clearable={false}
            />
            <ControlledSelect
              fieldName='engagementOriginatorId'
              label='Engagement Originator'
              options={attorneys}
              form={form}
              clearable={false}
            />
          </div>
        </>
      )}
      {feeType === 'Contingency' && (
        <>
          <div className={inputFieldStyles.inputRow}>
            <PercentInput
              form={form}
              name='contingencyFees.pctPreFilingOfLawsuit'
              label='% Pre-Filing of Lawsuit'
            />
            <PercentInput
              form={form}
              name='contingencyFees.pctUponFilingOfLawsuit'
              label='% Upon Filing of Lawsuit'
            />
            <PercentInput
              form={form}
              name='contingencyFees.pctUponCommencementOfTrial'
              label='% Upon Commencement of Trial'
              labelProps={{
                style: {
                  fontSize: 'var(--mantine-font-size-xs)',
                },
              }}
            />
          </div>
          <div className={inputFieldStyles.inputRow}>
            <ControlledSelect
              fieldName='referralOriginatorId'
              label='Referral Originator'
              options={attorneys}
              form={form}
              clearable={false}
            />
            <ControlledSelect
              fieldName='engagementOriginatorId'
              label='Engagement Originator'
              options={attorneys}
              form={form}
              clearable={false}
            />
          </div>
        </>
      )}
      {feeType === `Unknown` && (
        <>
          <div className={inputFieldStyles.inputRow}>
            <ControlledSelect
              fieldName='referralOriginatorId'
              label='Referral Originator'
              options={attorneys}
              form={form}
              clearable={false}
            />
            <ControlledSelect
              fieldName='engagementOriginatorId'
              label='Engagement Originator'
              options={attorneys}
              form={form}
              clearable={false}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default FeeTypeFields;

const InsuranceFeeTypeFields = ({ form }: { form: Form }) => {
  return (
    <>
      <InsuranceProviderSegmentedControl mantineForm={form} />
      <InsuranceCaseInstructions />
      <InsuranceCasesSection
        form={form as any}
        collectionPath='insuranceCases'
      />
    </>
  );
};

const InsuranceCaseInstructions = () => {
  return (
    <div style={{ textAlign: 'center', margin: '0.75rem 0' }}>
      <p style={{ margin: '0', fontSize: '0.9rem', fontWeight: '700' }}>
        If case is reduced fee out-of-pocket, choose Flat Fee or Hourly instead.
      </p>
      <p style={{ margin: '0', fontSize: '0.9rem', fontWeight: '700' }}>
        Verify coverage before assigning to attorney.
      </p>
    </div>
  );
};
